<script setup lang="ts">
import { OnClickOutside } from '@vueuse/components';
import { computed, ref } from 'vue';

import { ICompetitionWithLabel } from '@pssa/types';

import { useCompetitionStore } from '@/store/useCompetitionStore';

const props = defineProps<{
  competitions: ICompetitionWithLabel[];
  currentCompetition: ICompetitionWithLabel | undefined;
  isAuthorised?: boolean;
}>();

const isOpen = ref(false);
const store = useCompetitionStore();
const onToggleList = () => (isOpen.value = !isOpen.value);
const onChangeCompetition = (id: string) => store.changeCompetition(id);
const competitionPath = computed(() => (props.isAuthorised ? '/admin' : '') + '/competition/');
</script>

<template>
  <OnClickOutside @trigger="isOpen = false">
    <button
      @click.prevent="onToggleList"
      v-if="currentCompetition"
      :class="['date-picker', { 'is-open': isOpen }]"
      data-qa="date-picker"
    >
      {{ currentCompetition.label }}
      <ol data-qa="date-picker-options">
        <li v-for="{ _id, label } in competitions" :key="_id">
          <router-link
            :to="{ path: competitionPath + _id }"
            @click.prevent="onChangeCompetition(_id)"
            :class="{ current: _id === currentCompetition._id }"
          >
            {{ label }}
          </router-link>
        </li>
      </ol>
    </button>
  </OnClickOutside>
</template>

<style lang="scss" scoped>
.date-picker {
  background: linear-gradient(180deg, #fff, #f2f2f2);
  background-color: #fff;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 5px;
  color: #000;
  cursor: pointer;
  display: block;
  font:
    1.5em/1 'Open Sans Condensed',
    sans-serif;
  height: 45px;
  padding: 0 2rem;
  position: relative;
  text-align: center;
  width: 350px;
  z-index: 5;
  margin: 0 auto;

  &::before {
    content: '\f107';
    font-family: 'Font Awesome 5 Free';
    font-weight: 900;
    margin-top: -0.5em;
    position: absolute;
    right: 10px;
    text-shadow: none;
    top: 50%;
  }

  &.is-open {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

ol {
  -webkit-overflow-scrolling: touch;
  background-color: #fff;
  box-shadow: 0px 3px 5px #b7b7b7;
  clip: rect(1px, 1px, 1px, 1px);
  height: 1px;
  left: 0;
  list-style: none;
  margin: 0;
  max-height: 200px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  top: 44px;
  white-space: nowrap; /* added line */
  width: 1px;
  z-index: 10;

  .is-open & {
    width: 100%;
    overflow-y: auto;
    clip: unset;
    height: auto;
  }

  a {
    align-items: center;
    background-color: #fff;
    border-top: 1px solid #efefef;
    color: #a1a1a1;
    display: flex;
    font-size: 1.25rem;
    justify-content: space-between;
    margin: 0;
    padding: 0.5rem;
    position: relative;
    text-align: left;
    text-decoration: none;

    &:hover,
    &.current {
      background-color: #efefef;
      color: #000;

      &::after {
        color: #000;
      }
    }

    &:hover {
      background-color: #d8d8d8;
    }

    &::after {
      color: #a1a1a1;
      content: '\f105';
      font-family: 'Font Awesome 5 Free';
      font-weight: bold;
      text-shadow: none;
    }
  }
}
</style>
